'use client';

import type { ReactNode, ComponentProps } from 'react';

import { useId, useRef, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Popover from '@mui/material/Popover';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useResponsive } from 'src/hooks/use-responsive';

import { LINE, RADIUS, SHADOW } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { FilterSectionCard } from 'src/components/cmui/filters/filter-section-card';

import { hubChipSx } from './hub-chip';
import { HubFilterChips } from './hub-filter-chips';
import { summarizeSelection } from './hub-condition-summary';
import { HubToggleList, resetToggleItems, type HubToggleItem } from './hub-toggle-list';

import type { HubSelect, HubSelectOption } from './hub-select';

type HubSelectProps = ComponentProps<typeof HubSelect>;

/** 판의 한 열 폭(px) — 넓은 화면은 섹션을 열로 편다(md 2열 · lg 3열, 섹션 수를 넘지 않게) */
const COLUMN_WIDTH = 320;

/** 축 하나만 연 판의 폭(px) */
const SINGLE_WIDTH = 360;

/** 판 아래와 화면 바닥 사이 여백(px) */
const PANEL_BOTTOM_GAP = 16;

/** 요약 칩을 눌러 연 섹션을 칠해 두는 시간(ms) */
const FLASH_MS = 1200;

/**
 * 필터 축(갈래) 하나 — 화면이 정의를 넘기고, 줄과 판의 모양은 부품이 정한다.
 *
 * 판 섹션의 몸은 셋 중 하나다(앞의 것이 이긴다):
 * - `choices` — 고를 것을 칩으로 바로 편다(`HubFilterChips` · 많으면 «검색 · 더보기»)
 * - `toggles` — 켜고 끄는 줄(`HubToggleList`)
 * - `render` — 그 밖의 칸(수 입력칸 · 사이드바 섹션 · 계정 목록). 받는 `close` 는 축 하나만 연 판에서만 닫는다
 *
 * 🔑 값과 바꾸는 함수는 화면의 것이다(새 상태를 두지 않는다).
 * 🔑 `pinned` 축은 줄에 늘 요약 칩으로 선다(「국가: 한국」) — 누르면 그 축만 담은 판이 열린다. 나머지는 「필터」 판에 모인다.
 * ⚠️ `onClear` 가 없으면 요약 칩에 ×도, 섹션에 「전체 해제」도 없다 — 「전체」로 되돌릴 길이 없는 축이다.
 * ⚠️ `always` 는 늘 걸려 있는 축(「전체」가 없는 칸) — 요약 칩은 늘 파랗지만 「필터 · N」의 N 에는 넣지 않는다.
 */
export type HubFilterGroup = {
  key: string;
  /** 축 이름 — 쌍점 없이(「채널」) */
  label: string;
  /** 줄의 요약 칩에 적는 말(`summarizeSelection` · `summarizeCount`) */
  summary: string;
  /** 요약 칩 글자 앞 작은 아이콘(채널 아이콘 등) */
  summaryIcon?: ReactNode;
  /** 판 섹션 머리의 요약 — 없으면 `summary`(여럿 고르는 칸은 「N개 선택」) */
  sectionSummary?: string;
  /** 기본값에서 벗어났나 — 켜지면 요약 칩이 파랗게 선다 */
  active: boolean;
  always?: boolean;
  onClear?: () => void;
  /** 줄에 늘 꺼내 두는 축 — 화면마다 한두 개 */
  pinned?: boolean;
  choices?: {
    options: readonly HubSelectOption[];
    value: string | readonly string[];
    multiple?: boolean;
    /** 「전체」가 없는 칸 — 고른 칩을 다시 눌러도 풀리지 않는다 */
    hideEmpty?: boolean;
    onPick: (next: string) => void;
    onLocked?: (value: string) => void;
  };
  toggles?: readonly HubToggleItem[];
  render?: (close: () => void) => ReactNode;
  /** 섹션 바닥의 흐린 한 줄 — 이 축이 어디에 걸리는지 같은 안내 */
  note?: ReactNode;
  /** 닫혀 있어도 그려 둔다 — 이펙트로 값을 고르는 칸(채널 섹션) */
  keepMounted?: boolean;
  /** 몸이 제 섹션 카드를 가진 칸(사이드바 섹션 `FilterBasic`) — 카드를 겹쳐 두르지 않는다 */
  flush?: boolean;
  /** 섹션 머리의 되돌리기 말 — 없으면 「전체 해제」(처음 값으로 되돌리는 축은 「기본값으로」) */
  clearLabel?: string;
  /** 판에 이 축 하나만 서면(꺼내 둔 칩으로 연 판) 섹션 카드를 두르지 않는다 — 판 머리가 이미 이름을 적고, 되돌리기는 판 바닥 「초기화」다 */
  bareWhenAlone?: boolean;
};

/**
 * **필터 줄의 축 묶음** — 꺼내 둔 축의 요약 칩 · 「필터 · N」 · 걸린 나머지 축의 요약 칩.
 *
 * ```
 *   (국가: 한국 ×)(유형: 전체 ˅)  (⚙ 필터 · 2)(채널: Instagram ×)(언어: 영어 ×)
 *   └ 꺼내 둔 축(pinned) ─┘      └ 나머지 축을 모은 판 · 걸린 것만 요약 칩
 * ```
 *
 * 판(정본 필터 팝오버) — 불투명 흰 면 · 테두리 · 그림자:
 * ```
 *   필터                                   ⓧ
 *   ┌ 채널  전체 · 전체 해제 ˄┐ ┌ 국가 …┐ ┌ …┐     ← md 2열 · lg 3열 · 폰은 하단 시트 1열
 *   │ (칩)(칩)                │ │      │ │   │
 *   초기화                                [닫기]
 * ```
 *
 * 🔑 **즉시 적용**이다 — 칩을 누르면 바로 걸린다. 판 아래는 「초기화」「닫기」뿐이고 「적용」이 없다.
 *   여러 축을 이어 고를 수 있게 판은 고른 뒤에도 열려 있다.
 * 🔑 요약 칩을 누르면 판이 그 섹션으로 굴러가 잠깐 칠해진다 · ×는 그 축만 푼다(`onClear`).
 * 🚨 판 면은 **불투명**이다 — 테마의 팝오버 면(반투명 유리)을 덮는다. 뒤 이미지가 비치면 칩 글자가 안 읽힌다.
 * ⚠️ `keepMounted` 축은 판이 닫혀 있어도(다른 판이 열려 있어도) 숨긴 채 그려 둔다 — 채널 섹션의 이펙트가 멈추면 안 된다.
 */
export function HubFilterGroups({
  groups,
  narrowed,
  onReset,
}: {
  groups: readonly HubFilterGroup[];
  /** 화면이 센 걸린 수 — 판 아래 「초기화」를 살릴지 */
  narrowed: number;
  onReset: () => void;
}) {
  const isPhone = useResponsive('down', 'sm');
  const isMdUp = useResponsive('up', 'md');
  const isLgUp = useResponsive('up', 'lg');
  const panelId = useId();
  const [panel, setPanel] = useState<{ anchor: HTMLElement; only: string | null; focus: string | null } | null>(null);
  /** 판이 쓸 수 있는 높이 — 단추 아래부터 화면 바닥까지(내용이 짧으면 내용 높이) */
  const [room, setRoom] = useState<number | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const pinned = groups.filter((group) => group.pinned);
  const rest = groups.filter((group) => !group.pinned);
  const count = rest.filter((group) => group.active && !group.always).length;
  const tags = rest.filter((group) => group.active);

  const open = Boolean(panel);
  const only = panel?.only ? groups.find((group) => group.key === panel.only) ?? null : null;
  /** 지금 판에 서는 섹션 */
  const inView = (group: HubFilterGroup) => open && (only ? group.key === only.key : !group.pinned);
  const shownCount = only ? 1 : rest.length;
  const lgColumns = Math.max(1, Math.min(3, shownCount));
  const mdColumns = Math.max(1, Math.min(2, shownCount));
  /** 지금 폭의 열 수 — 폰은 1열 */
  const columns = isLgUp ? lgColumns : isMdUp ? mdColumns : 1;

  const openAt = (target: HTMLElement, onlyKey: string | null, focus: string | null) => {
    setRoom(window.innerHeight - target.getBoundingClientRect().bottom - PANEL_BOTTOM_GAP);
    setPanel({ anchor: target, only: onlyKey, focus });
  };
  const close = () => setPanel(null);

  useEffect(() => {
    const focus = panel?.focus;
    if (!focus) return undefined;
    /* ⚠️ 판이 커지는 전환이 끝난 뒤에 굴린다 — 전환 중의 `scrollIntoView` 는 먹지 않는다. 구르는 통만 굴린다(화면은 그대로) */
    const timer = window.setTimeout(() => {
      const section = sectionRefs.current[focus];
      const box = section?.closest('[data-filter-body]');
      if (section && box) box.scrollTop += section.getBoundingClientRect().top - box.getBoundingClientRect().top - 8;
      setFlashKey(focus);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [panel]);

  useEffect(() => {
    if (!flashKey) return undefined;
    const timer = window.setTimeout(() => setFlashKey(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashKey]);

  const sectionOf = (group: HubFilterGroup) => {
    const clearAll = group.onClear && group.active && !group.always ? group.onClear : null;
    const bodyClose = only ? close : noop;
    let body: ReactNode;
    if (group.choices) {
      body = (
        <HubFilterChips
          label={group.label}
          options={group.choices.options}
          value={group.choices.value}
          multiple={group.choices.multiple}
          hideEmpty={group.choices.hideEmpty}
          onPick={group.choices.onPick}
          onLocked={group.choices.onLocked}
        />
      );
    } else if (group.toggles) {
      body = <HubToggleList items={group.toggles} />;
    } else {
      body = group.render?.(bodyClose) ?? null;
    }
    if (group.flush) return body;
    const note = group.note && <Typography sx={{ typography: 'uiMeta', color: 'text.disabled', mt: 1 }}>{group.note}</Typography>;
    if (only && group.bareWhenAlone) {
      return (
        <>
          {body}
          {note}
        </>
      );
    }
    return (
      <FilterSectionCard title={group.label} summary={group.sectionSummary ?? group.summary} onClearAll={clearAll} clearLabel={group.clearLabel}>
        {body}
        {note}
      </FilterSectionCard>
    );
  };

  /** 그릴 섹션과 그 열 — 열 차례로 늘어놓는다(열의 첫 섹션이 열을 끊는다) · 판에 안 선 `keepMounted` 축은 열 -1(숨김) */
  const visibleGroups = groups.filter(inView);
  const placed = [
    ...Array.from({ length: columns }, (_unused, column) => visibleGroups
      .filter((_group, index) => index % columns === column)
      .map((group, index) => ({ group, column, first: index === 0 }))).flat(),
    ...groups.filter((group) => group.keepMounted && !inView(group)).map((group) => ({ group, column: -1, first: false })),
  ];

  const resetHere = only ? (only.active && !only.always ? only.onClear : undefined) : narrowed > 0 ? onReset : undefined;

  const panelBody = (
    <Stack sx={{ flex: '1 1 auto', minHeight: 0 }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, borderBottom: 1, borderColor: LINE.edge }}>
        <Typography sx={{ typography: 'uiStrong', flex: 1 }}>{only ? only.label : '필터'}</Typography>
        <IconButton size="small" aria-label="필터 닫기" onClick={close}>
          <Iconify icon="solar:close-circle-linear" width={18} />
        </IconButton>
      </Stack>

      {/*
        🔑 넓으면 열로 편다 — 섹션을 차례대로 열에 하나씩 돌려 담는다(짧은 섹션끼리 한 열에 쌓인다).
          열마다 첫 섹션에서 열을 끊는다(`breakBefore`) — 그냥 다단으로 두면 높이를 맞추느라 열을 비워 둔다.
        ⚠️ 섹션은 늘 **한 부모 아래 같은 열쇠**로 선다 — 판에 안 선 `keepMounted` 축(숨김)이 판에 들어올 때
          다시 마운트되면 섹션이 쥔 화면 상태(채널의 「전체 해제」 표시)가 날아간다.
      */}
      <Box
        data-filter-body
        sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', p: 1.5, columnCount: columns, columnGap: 1.5 }}
      >
        {placed.map(({ group, column, first }) => {
          const visible = column >= 0;
          return (
            <Box
              key={group.key}
              ref={(el: HTMLElement | null) => {
                sectionRefs.current[group.key] = el;
              }}
              data-filter-group={group.key}
              sx={{
                display: visible ? 'block' : 'none',
                breakInside: 'avoid',
                breakBefore: visible && first && column > 0 ? 'column' : 'auto',
                mb: 1.5,
                borderRadius: RADIUS.lg,
                transition: 'box-shadow .3s',
                boxShadow: flashKey === group.key ? (theme) => `0 0 0 2px ${theme.palette.primary.light}` : 'none',
              }}
            >
              {sectionOf(group)}
            </Box>
          );
        })}
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderTop: 1, borderColor: LINE.edge }}>
        <Button
          size="small"
          color="inherit"
          disabled={!resetHere}
          onClick={resetHere}
          startIcon={<Iconify icon="solar:restart-linear" width={14} />}
        >
          초기화
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" color="inherit" onClick={close}>
          닫기
        </Button>
      </Stack>
    </Stack>
  );

  const hasKeepMounted = groups.some((group) => group.keepMounted);
  const paperSurface = {
    id: panelId,
    role: 'dialog',
    'aria-label': only ? only.label : '필터',
  };

  return (
    <>
      {pinned.map((group) => (
        <SummaryChip key={group.key} group={group} onOpen={(target) => openAt(target, group.key, null)} />
      ))}

      {rest.length > 0 && (
        <Button
          size="small"
          aria-haspopup="dialog"
          aria-expanded={open && !only}
          aria-controls={open && !only ? panelId : undefined}
          onClick={(event) => openAt(event.currentTarget, null, null)}
          startIcon={<Iconify icon="solar:filter-linear" width={14} />}
          sx={hubChipSx(count > 0)}
        >
          {count > 0 ? `필터 · ${count}` : '필터'}
        </Button>
      )}

      {/* 걸린 축은 «태그»로 — 늘 보이는 축 칩(테두리)과 모양으로 갈리고, 「필터」 단추와 가는 선으로 띈다 */}
      {tags.length > 0 && <Box aria-hidden sx={TAG_DIVIDER_SX} />}
      {tags.map((group) => (
        <SummaryChip tag key={group.key} group={group} onOpen={(target) => openAt(target, null, group.key)} />
      ))}

      {isPhone ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          keepMounted={hasKeepMounted}
          slotProps={{
            paper: {
              ...paperSurface,
              sx: { ...PANEL_SURFACE_SX, maxHeight: '85vh', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
            },
          }}
        >
          {panelBody}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={panel?.anchor ?? null}
          onClose={close}
          keepMounted={hasKeepMounted}
          marginThreshold={8}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              ...paperSurface,
              sx: {
                ...PANEL_SURFACE_SX,
                mt: 0.5,
                width: only
                  ? SINGLE_WIDTH
                  : { sm: COLUMN_WIDTH + 120, md: COLUMN_WIDTH * mdColumns + 24, lg: COLUMN_WIDTH * lgColumns + 24 },
                maxWidth: 'calc(100vw - 16px)',
                /* 🔑 픽셀로 자르지 않는다 — 단추 아래부터 화면 바닥까지가 한도, 내용이 짧으면 내용 높이 */
                maxHeight: room != null && room > 280 ? room : `calc(100dvh - ${PANEL_BOTTOM_GAP * 2}px)`,
              },
            },
          }}
        >
          {panelBody}
        </Popover>
      )}
    </>
  );
}

/**
 * 고르개(`HubSelect`) 정의를 축으로 — 판에서는 같은 선택지를 칩으로 바로 편다.
 *
 * 🔑 요약 말과 칠할지는 고르개와 **같은 규칙**이다(`summarizeSelection` · 「골라서 좁혀졌나」) — `summary`·`active` 를 주면 그것을 따른다.
 * 🔑 여럿 고르는 칸의 섹션 머리는 「N개 선택」이다.
 * @param def 고르개 속성 + 축 이름(`label`)·×(`onClear`)·늘 걸림(`always`)·꺼내 둠(`pinned`)
 */
export function hubSelectGroup({
  key,
  label,
  onClear,
  always,
  pinned,
  summary,
  ...select
}: Omit<HubSelectProps, 'prefix' | 'block' | 'renderLabel'> & {
  key: string;
  label: string;
  onClear?: () => void;
  always?: boolean;
  pinned?: boolean;
  summary?: string;
}): HubFilterGroup {
  const picked = select.active ?? (Array.isArray(select.value) ? select.value.length > 0 : Boolean(select.value) && !select.hideEmpty);
  const line = summary ?? summarizeSelection(select.value, select.options, select.empty);
  const many = Array.isArray(select.value) && select.multiple;
  return {
    key,
    label,
    summary: line,
    sectionSummary: many ? (select.value.length > 0 ? `${select.value.length}개 선택` : select.empty) : line,
    active: picked,
    always,
    pinned,
    onClear,
    choices: {
      options: select.disabled ? select.options.map((option) => ({ ...option, disabled: true })) : select.options,
      value: select.value,
      multiple: select.multiple,
      hideEmpty: select.hideEmpty,
      onPick: select.onChange,
      onLocked: select.onLocked,
    },
  };
}

/**
 * 켜고 끄는 줄 여럿을 축 하나로 — 판에서는 한 섹션에 토글 줄로 선다(「수집 기준」·「집계 기준」).
 *
 * 🔑 걸림 = 처음 값(`defaultOn`)과 다른 줄이 있나 · 요약은 「기본」/「N개 바뀜」 · 섹션 머리 되돌리기·×는 처음 값으로(`resetToggleItems`).
 * @param def 축 열쇠·이름·줄 목록 · `clearLabel` 섹션 머리 되돌리기 말(없으면 「전체 해제」)
 */
export function hubToggleGroup({
  key,
  label,
  items,
  clearLabel,
}: {
  key: string;
  label: string;
  items: readonly HubToggleItem[];
  clearLabel?: string;
}): HubFilterGroup {
  const changed = items.filter((item) => item.defaultOn !== undefined && item.checked !== item.defaultOn).length;
  return {
    key,
    label,
    summary: changed > 0 ? `${changed}개 바뀜` : '기본',
    active: changed > 0,
    onClear: () => resetToggleItems(items),
    toggles: items,
    ...(clearLabel ? { clearLabel } : {}),
  };
}

// ---- 이하 비공개 ----------------------------------------------------------

/**
 * 판 면 — 불투명 흰 면 · 테두리 · 떠 있는 그림자.
 * 🚨 테마 팝오버의 유리 면(반투명 + 흐림 + 모서리 무늬)을 여기서 걷는다 — 테마는 다른 팝오버 전부의 것이라 건드리지 않는다.
 */
const PANEL_SURFACE_SX = {
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'background.paper',
  backgroundImage: 'none',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  border: `1px solid ${LINE.edge}`,
  boxShadow: SHADOW.overlay,
  borderRadius: RADIUS.lg,
  p: 0,
} as const;

/**
 * 「필터」 뒤 걸린 축의 **태그** — 테두리 없는 연한 파랑 · 칩보다 한 단계 낮다(24).
 * 🔑 꺼내 둔 축의 요약 칩(테두리 · 28)과 모양으로 갈린다 — «늘 보이는 축»과 «판에서 건 것»이 한눈에 다르다.
 */
const TAG_SX = {
  height: 24,
  borderRadius: RADIUS.sm,
  /* ⚠️ `border` 줄임말이 아니라 폭으로 끈다 — 칩 sx 의 `border` 열쇠가 앞자리에 남아 뒤의 `borderStyle` 에 진다 */
  borderWidth: 0,
  bgcolor: 'primary.lighter',
  color: 'primary.dark',
} as const;

/** 「필터」 단추와 태그 묶음 사이의 가는 세로 선 */
const TAG_DIVIDER_SX = { width: '1px', height: 16, bgcolor: LINE.edge, flexShrink: 0, alignSelf: 'center' } as const;

/** 줄의 요약 칩 — 「국가: 한국」 · 걸렸으면 파랗게 + ×, 안 걸렸으면 펼침 화살표 · `tag` 는 「필터」 뒤 걸린 축의 태그 모양 */
function SummaryChip({ group, onOpen, tag = false }: { group: HubFilterGroup; onOpen: (target: HTMLElement) => void; tag?: boolean }) {
  const clearable = Boolean(group.onClear) && group.active && !group.always;
  return (
    <Box
      data-filter-chip={group.key}
      sx={{
        ...hubChipSx(group.active),
        display: 'inline-flex',
        alignItems: 'center',
        borderStyle: 'solid',
        px: 0,
        overflow: 'hidden',
        ...(tag ? TAG_SX : null),
      }}
    >
      <ButtonBase
        aria-haspopup="dialog"
        onClick={(event) => onOpen(event.currentTarget.parentElement ?? event.currentTarget)}
        sx={{ height: 1, pl: tag ? 1 : 1.5, pr: clearable ? 0.5 : 1, gap: 0.5, typography: 'uiAction', whiteSpace: 'nowrap' }}
      >
        <Box component="span" sx={{ color: tag ? 'inherit' : 'text.secondary', opacity: tag ? 0.75 : 1, fontWeight: 400 }}>{`${group.label}:`}</Box>
        {group.summaryIcon}
        {group.summary}
        {!clearable && <Iconify icon="solar:alt-arrow-down-linear" width={14} sx={{ opacity: 0.6 }} />}
      </ButtonBase>
      {clearable && (
        <IconButton
          size="small"
          aria-label={`${group.label} 조건 풀기`}
          onClick={group.onClear}
          sx={{ width: tag ? 20 : 24, height: tag ? 20 : 24, mr: 0.5, color: 'inherit' }}
        >
          <Iconify icon="solar:close-circle-linear" width={14} />
        </IconButton>
      )}
    </Box>
  );
}

/** 모아 연 판에서는 고른 뒤에도 판을 닫지 않는다 — 여러 축을 이어 고른다 */
function noop() {}
